/**
 * Minimal authorized fetch for the agentic surface.
 *
 * Sends the Cognito/local JWT as `Authorization: Bearer` (same contract as
 * frontend/src/api/apiClient.ts; the backend's TenantSecurityMiddleware
 * authenticates it and resolves the tenant from custom:tenant_id) and keeps
 * `credentials: 'include'` so the local-dev httpOnly-cookie path works too.
 *
 * CSRF: because cookies travel, every request ALSO carries the
 * `X-Requested-With` header (frontend/src/api/csrfHeader.ts contract). The
 * backend's csrf_gate (backend/app/middleware/cookie_auth.py, applied by
 * CookieAuthMiddleware and TenantSecurityMiddleware's cookie fallback)
 * rejects any cookie-authenticated POST/PUT/PATCH/DELETE without it with
 * 403 {"error":"csrf_protection"}. Sent on GETs too so no request shape is
 * ever the odd one out. Never send it to third-party origins.
 *
 * Errors surface as ApiError with the HTTP status attached — the UI maps
 * 403/404 from the agentic_platform-gated endpoints to the clean
 * "not enabled for your tenant" state (the backend uses
 * require_features_or_404: unentitled tenants see 404, retain-and-hide).
 */
import { getGlobalAuthToken } from '../auth/tokenStore';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');

export const CSRF_HEADER_NAME = 'X-Requested-With';
export const CSRF_HEADER_VALUE = 'XMLHttpRequest';

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** True when the error means "this surface is not enabled for the tenant". */
export function isNotEntitled(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 403 || error.status === 404);
}

function baseHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
  };
  const token = getGlobalAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function request<T>(endpoint: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...init,
    credentials: 'include',
  });

  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body?.detail && typeof body.detail === 'string') detail = body.detail;
    } catch {
      // non-JSON error body — keep the generic message
    }
    throw new ApiError(res.status, detail);
  }

  return (await res.json()) as T;
}

export async function apiGet<T>(endpoint: string): Promise<T> {
  return request<T>(endpoint, { method: 'GET', headers: baseHeaders() });
}

/**
 * JSON POST. Used ONLY by the governance mutations (dual-control decide,
 * Safe-Mode enter / exit request / exit) — every one of them is
 * permission-gated and audited server-side; the client never decides
 * anything on its own.
 */
export async function apiPost<T>(endpoint: string, body: unknown): Promise<T> {
  return request<T>(endpoint, {
    method: 'POST',
    headers: { ...baseHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}
